import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type ExcerptusPrompt = Tables<"composer_excerptus_prompts">;
export type UserPrompt = Tables<"composer_user_prompts">;

// ---- Excerptus prompts (admin-curated, read-only for end users) ----

export const fetchPublishedExcerptusPrompts = async (): Promise<ExcerptusPrompt[]> => {
  const { data, error } = await supabase
    .from("composer_excerptus_prompts")
    .select("*")
    .eq("is_published", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const fetchAllExcerptusPrompts = async (): Promise<ExcerptusPrompt[]> => {
  const { data, error } = await supabase
    .from("composer_excerptus_prompts")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const createExcerptusPrompt = async (
  values: Partial<TablesInsert<"composer_excerptus_prompts">> & { title: string; body: string },
): Promise<ExcerptusPrompt> => {
  const { data: userResp } = await supabase.auth.getUser();
  const payload: TablesInsert<"composer_excerptus_prompts"> = {
    title: values.title,
    body: values.body,
    description: values.description ?? null,
    tags: values.tags ?? [],
    model_hint: values.model_hint ?? null,
    is_published: values.is_published ?? false,
    sort_order: values.sort_order ?? 0,
    slug: values.slug ?? null,
    created_by_user_id: userResp.user?.id ?? null,
  };
  const { data, error } = await supabase
    .from("composer_excerptus_prompts")
    .insert(payload)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const updateExcerptusPrompt = async (
  id: string,
  values: TablesUpdate<"composer_excerptus_prompts">,
): Promise<ExcerptusPrompt> => {
  const { data, error } = await supabase
    .from("composer_excerptus_prompts")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const deleteExcerptusPrompt = async (id: string): Promise<void> => {
  const { error } = await supabase.from("composer_excerptus_prompts").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ---- User prompts (workspace-scoped) ----

export const fetchUserPrompts = async (workspaceId: string): Promise<UserPrompt[]> => {
  const { data, error } = await supabase
    .from("composer_user_prompts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const createUserPrompt = async (
  workspaceId: string,
  values: {
    title: string;
    body: string;
    description?: string | null;
    tags?: string[];
    model_hint?: string | null;
    cloned_from_excerptus_id?: string | null;
  },
): Promise<UserPrompt> => {
  const { data: userResp } = await supabase.auth.getUser();
  if (!userResp.user) throw new Error("Not signed in");
  const payload: TablesInsert<"composer_user_prompts"> = {
    workspace_id: workspaceId,
    created_by_user_id: userResp.user.id,
    title: values.title,
    body: values.body,
    description: values.description ?? null,
    tags: values.tags ?? [],
    model_hint: values.model_hint ?? null,
    cloned_from_excerptus_id: values.cloned_from_excerptus_id ?? null,
  };
  const { data, error } = await supabase
    .from("composer_user_prompts")
    .insert(payload)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const updateUserPrompt = async (
  id: string,
  values: TablesUpdate<"composer_user_prompts">,
): Promise<UserPrompt> => {
  const { data, error } = await supabase
    .from("composer_user_prompts")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const deleteUserPrompt = async (id: string): Promise<void> => {
  const { error } = await supabase.from("composer_user_prompts").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

/** Persist a new ordering of the workspace's user prompts. Updates `sort_order` per id. */
export const reorderUserPrompts = async (
  workspaceId: string,
  orderedIds: string[],
): Promise<void> => {
  await Promise.all(
    orderedIds.map((id, index) =>
      supabase
        .from("composer_user_prompts")
        .update({ sort_order: index + 1 })
        .eq("id", id)
        .eq("workspace_id", workspaceId),
    ),
  );
};

export const duplicateExcerptusPromptToMine = async (
  prompt: ExcerptusPrompt,
  workspaceId: string,
): Promise<UserPrompt> => {
  return createUserPrompt(workspaceId, {
    title: `${prompt.title} (copy)`,
    body: prompt.body,
    description: prompt.description,
    tags: prompt.tags ?? [],
    model_hint: prompt.model_hint,
    cloned_from_excerptus_id: prompt.id,
  });
};

/**
 * Paste-as-is helper: extract the first non-empty line as a title candidate.
 * Returns null if the body has no usable first line.
 * Does NOT mutate the body — caller decides whether to strip.
 */
export const extractFirstLineTitle = (body: string): string | null => {
  const lines = body.split(/\r?\n/);
  for (const line of lines) {
    const trimmed = line.replace(/^#+\s*/, "").trim();
    if (trimmed.length > 0) return trimmed.slice(0, 200);
  }
  return null;
};
